import React, {useEffect, useRef, useState} from 'react';
import {TouchableOpacity, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCText from '@/Atoms/CCText';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';

const CATEGORY_ICONS: Record<string, string> = {
  Class: 'school-outline',
  Subscription: 'card-outline',
  Coach: 'person-outline',
  Payment: 'cash-outline',
  Assignment: 'document-text-outline',
  Platform: 'phone-portrait-outline',
  Recording: 'videocam-outline',
  Tournament: 'trophy-outline',
  League: 'ribbon-outline',
  Mentor: 'compass-outline',
  Support: 'help-buoy-outline',
  Onboarding: 'rocket-outline',
  'Message Founder': 'megaphone-outline',
  'Batch Issues': 'calendar-outline',
  Other: 'ellipsis-horizontal-circle-outline',
};
const DEFAULT_ICON = 'help-circle-outline';
const MESSAGE_FOUNDER_CATEGORY = 'Message Founder';

interface CategoryStepProps {
  issueCategories: any;
  isLoading: boolean;
  setBugType: (v: any) => void;
  setBugCategory: (v: any) => void;
  onBothSelected: (subCategoryId: string, subCategoryLabel: string) => void;
}

const CategoryStep: React.FC<CategoryStepProps> = ({
  issueCategories,
  isLoading,
  setBugType,
  setBugCategory,
  onBothSelected,
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const subCategoryPanelRef = useRef<any>(null);

  const categoryNames =
    issueCategories && typeof issueCategories === 'object'
      ? Object.keys(issueCategories).filter(
          name => name !== MESSAGE_FOUNDER_CATEGORY,
        )
      : [];
  const selectedSubCats = selected
    ? issueCategories[selected]?.sub_categories || []
    : [];
  // "Message Founder" is pulled out of the grid and shown as a small
  // separate link instead -- it's a direct escalation path, not a
  // self-serve category like the others.
  const founderSubCategories =
    issueCategories?.[MESSAGE_FOUNDER_CATEGORY]?.sub_categories || [];
  const founderEntry = founderSubCategories[0]
    ? (Object.entries(founderSubCategories[0])[0] as [string, string])
    : null;

  useEffect(() => {
    if (selected) {
      subCategoryPanelRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selected]);

  const selectSubCategory = (
    categoryName: string,
    subCategoryId: string,
    subCategoryLabel: string,
  ) => {
    setBugType({label: categoryName, value: categoryName});
    setBugCategory(subCategoryId);
    onBothSelected(subCategoryId, subCategoryLabel);
  };

  return (
    <View>
      <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
        <Ionicons name="help-buoy-outline" size={22} color={CCColors?.Green} />
        <CCText fontWeight="bold" fontSize={FontSizes[16]}>
          What do you need help with?
        </CCText>
      </View>
      <CCText
        fontSize={FontSizes[12]}
        color={CCColors?.TextColor?.Grey}
        style={{marginTop: vs(4)}}>
        Pick a category and we'll show you a quick answer first — most things
        don't need a ticket.
      </CCText>

      {isLoading && !categoryNames.length && (
        <CCText
          fontSize={FontSizes[13]}
          color={CCColors?.TextColor?.Grey}
          style={{marginTop: vs(20)}}>
          Loading categories…
        </CCText>
      )}

      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          marginTop: vs(18),
          marginHorizontal: -5,
        }}>
        {categoryNames.map(categoryName => {
          const isSelected = selected === categoryName;
          return (
            <TouchableOpacity
              key={categoryName}
              onPress={() => setSelected(isSelected ? null : categoryName)}
              style={{
                width: '25%',
                paddingHorizontal: 5,
                marginBottom: vs(12),
                alignItems: 'center',
              }}>
              <View
                style={{
                  width: '100%',
                  alignItems: 'center',
                  paddingVertical: vs(14),
                  paddingHorizontal: 6,
                  borderRadius: 12,
                  borderWidth: isSelected ? 2 : 1,
                  borderColor: isSelected
                    ? CCColors?.Green
                    : CCColors?.BorderLight,
                  backgroundColor: isSelected
                    ? updateColorWithOpacity(CCColors?.Green, 0.07)
                    : CCColors?.White,
                }}>
                <View
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 23,
                    backgroundColor: updateColorWithOpacity(
                      CCColors?.Green,
                      0.12,
                    ),
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: vs(8),
                  }}>
                  <Ionicons
                    name={CATEGORY_ICONS[categoryName] || DEFAULT_ICON}
                    size={22}
                    color={CCColors?.Green}
                  />
                </View>
                <CCText
                  fontWeight="bold"
                  fontSize={FontSizes[12]}
                  textAlign="center"
                  numberOfLines={2}>
                  {categoryName}
                </CCText>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>

      {selected && (
        <View
          ref={subCategoryPanelRef}
          style={{
            borderWidth: 1,
            borderColor: CCColors?.BorderLight,
            borderRadius: 10,
            overflow: 'hidden',
          }}>
          <View
            style={{
              paddingVertical: vs(10),
              paddingHorizontal: 14,
              backgroundColor: updateColorWithOpacity(CCColors?.Green, 0.07),
            }}>
            <CCText fontWeight="bold" fontSize={FontSizes[13]}>
              {selected}
            </CCText>
          </View>
          {selectedSubCats.map((item: any) => {
            const entry = Object.entries(item || {})[0] as
              | [string, string]
              | undefined;
            if (!entry) {
              return null;
            }
            const [subId, subLabel] = entry;
            return (
              <TouchableOpacity
                key={subId}
                onPress={() => selectSubCategory(selected, subId, subLabel)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  paddingVertical: vs(11),
                  paddingHorizontal: 14,
                  borderTopWidth: 1,
                  borderTopColor: CCColors?.BorderLight,
                }}>
                <CCText fontSize={FontSizes[13]} style={{flex: 1}}>
                  {subLabel}
                </CCText>
                <Ionicons
                  name="chevron-forward"
                  size={15}
                  color={CCColors?.TextColor?.Grey}
                />
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {founderEntry && (
        <TouchableOpacity
          onPress={() =>
            selectSubCategory(
              MESSAGE_FOUNDER_CATEGORY,
              founderEntry[0],
              founderEntry[1],
            )
          }
          style={{alignSelf: 'flex-end', marginTop: vs(14)}}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
            <Ionicons
              name="megaphone-outline"
              size={14}
              color={CCColors?.TextColor?.Grey}
            />
            <CCText
              fontSize={FontSizes[12]}
              color={CCColors?.TextColor?.Grey}
              style={{textDecorationLine: 'underline'}}>
              Message the founder
            </CCText>
          </View>
        </TouchableOpacity>
      )}
    </View>
  );
};

export default CategoryStep;
